import { getAuditLogColumns } from "./columns";
import {
  AuditLogChange,
  ExtendedUserAuditLog,
  UserAuditLogsProps,
} from "./types";
import { useUserAuditLogsQuery } from "@/common/api/services/userAuditLogs";
import { DataTablePagination } from "@/components/shared/DataTable/DataTablePagination";
import { DataTableViewOptions } from "@/components/shared/DataTable/DataTableViewOptions";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
  VisibilityState,
} from "@tanstack/react-table";
import { format } from "date-fns";
import { enUS } from "date-fns/locale";
import { AlertCircle, AlertTriangle, Loader2 } from "lucide-react";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

interface AvatarPreviewProps {
  avatarData: {
    fullSize?: {
      url: string;
      width: number;
      height: number;
    };
  } | null;
  className?: string;
}

const AvatarPreview = ({ avatarData, className }: AvatarPreviewProps) => {
  if (!avatarData?.fullSize?.url) {
    return (
      <Avatar className={cn("h-16 w-16", className)}>
        <AvatarFallback>N/A</AvatarFallback>
      </Avatar>
    );
  }

  return (
    <Avatar className={cn("h-16 w-16", className)}>
      <AvatarImage src={avatarData.fullSize.url} alt="Avatar" />
      <AvatarFallback>N/A</AvatarFallback>
    </Avatar>
  );
};

interface CodeViewProps {
  code: any;
}

const CodeView = ({ code }: CodeViewProps) => {
  return (
    <div className="bg-zinc-950 rounded-lg p-4">
      <pre className="rounded-lg overflow-auto max-h-[400px]">
        <code className="relative z-50 text-white">
          {JSON.stringify(code, null, 2)}
        </code>
      </pre>
    </div>
  );
};

export const UserAuditLogs = ({ user }: UserAuditLogsProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [selectedLog, setSelectedLog] = useState<ExtendedUserAuditLog | null>(
    null,
  );
  const [selectedCode, setSelectedCode] = useState<{
    title: string;
    code: any;
  } | null>(null);
  const { auditLogsQuery, deleteAuditLogMutation } = useUserAuditLogsQuery();

  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [deleteConfirm, setDeleteConfirm] = useState<{
    id: string;
    isOpen: boolean;
  }>({ id: "", isOpen: false });

  const {
    data: logs,
    isLoading,
    error,
  } = useQuery({
    ...auditLogsQuery({
      userId: user.userUUID,
      page,
      size: pageSize,
    }),
    retry: 1,
    refetchOnWindowFocus: false,
  });

  const { mutate: deleteLog, isPending: isDeleting } = useMutation({
    ...deleteAuditLogMutation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user-audit-logs"] });
      toast({
        title: t("settings.auditLogs.toast.deleteSuccess.title"),
        description: t("settings.auditLogs.toast.deleteSuccess.description"),
      });
      setDeleteConfirm({ id: "", isOpen: false });
    },
    onError: () => {
      toast({
        variant: "destructive",
        title: t("settings.auditLogs.toast.deleteError.title"),
        description: t("settings.auditLogs.toast.deleteError.description"),
      });
      setDeleteConfirm({ id: "", isOpen: false });
    },
  });

  const handleDeleteClick = (id: string) => {
    setDeleteConfirm({ id, isOpen: true });
  };

  const handleDeleteConfirm = () => {
    if (deleteConfirm.id) {
      deleteLog(deleteConfirm.id);
    }
  };

  const getActionBadgeVariant = (
    action: string,
  ): "default" | "destructive" | "secondary" | "outline" => {
    switch (action) {
      case "UPDATE_PROFILE":
        return "default";
      case "DELETE_PROFILE":
        return "destructive";
      case "LOGIN":
        return "secondary";
      case "LOGOUT":
        return "outline";
      default:
        return "default";
    }
  };

  const formatValue = (value: any): ReactNode => {
    if (value === null || value === undefined) {
      return t("settings.auditLogs.details.noData");
    }

    if (typeof value === "object") {
      if (value.fullSize?.url) {
        return <AvatarPreview avatarData={value} />;
      }

      return Object.entries(value)
        .map(([key, val]) => `${key}: ${val}`)
        .join(", ");
    }

    return String(value);
  };

  const renderChanges = (details: { changes?: AuditLogChange[] }) => {
    if (!details?.changes) return null;
    return details.changes.map((change, index) => (
      <div key={index} className="space-y-1">
        <div className="font-medium">
          {t(`settings.auditLogs.fields.${change.field}`) || change.field}
        </div>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div className="space-y-1">
            <div className="text-muted-foreground">
              {t("settings.auditLogs.details.oldValue")}:
            </div>
            <div className="rounded-md bg-muted p-2 whitespace-pre-wrap break-words flex items-center justify-center">
              {formatValue(change.oldValue)}
            </div>
            {typeof change.oldValue === "object" && (
              <Button
                variant="outline"
                size="sm"
                className="w-full mt-2"
                onClick={() =>
                  setSelectedCode({
                    title: t("settings.auditLogs.details.oldValue"),
                    code: change.oldValue,
                  })
                }
              >
                {t("settings.auditLogs.details.showCode")}
              </Button>
            )}
          </div>
          <div className="space-y-1">
            <div className="text-muted-foreground">
              {t("settings.auditLogs.details.newValue")}:
            </div>
            <div className="rounded-md bg-muted p-2 whitespace-pre-wrap break-words flex items-center justify-center">
              {formatValue(change.newValue)}
            </div>
            {typeof change.newValue === "object" && (
              <Button
                variant="outline"
                size="sm"
                className="w-full mt-2"
                onClick={() =>
                  setSelectedCode({
                    title: t("settings.auditLogs.details.newValue"),
                    code: change.newValue,
                  })
                }
              >
                {t("settings.auditLogs.details.showCode")}
              </Button>
            )}
          </div>
        </div>
      </div>
    ));
  };

  const formatDate = (dateString: string | undefined | null): string => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "N/A";
      return format(date, "d MMMM yyyy HH:mm", { locale: enUS });
    } catch {
      return "N/A";
    }
  };

  const columns = getAuditLogColumns(
    t,
    (log: ExtendedUserAuditLog) => setSelectedLog(log),
    handleDeleteClick,
  );

  const extendedLogs = (logs?.content || []).map((log) => {
    const detailsObj = log.details as Record<string, any>;

    return {
      ...log,
      details: {
        changes: Array.isArray(detailsObj?.changes) ? detailsObj.changes : [],
        entityType: detailsObj?.entityType || "",
        entityId: detailsObj?.entityId || "",
        timestamp: detailsObj?.timestamp || log.createdAt,
        additionalInfo: detailsObj?.additionalInfo,
      },
    } as ExtendedUserAuditLog;
  });

  const table = useReactTable<ExtendedUserAuditLog>({
    data: extendedLogs,
    columns,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting,
      columnVisibility,
      pagination: {
        pageIndex: page,
        pageSize: pageSize,
      },
    },
    pageCount: logs?.totalPages || 0,
    manualPagination: true,
    onPaginationChange: (updater) => {
      if (typeof updater === "function") {
        const newState = updater({ pageIndex: page, pageSize });
        setPage(newState.pageIndex);
        setPageSize(newState.pageSize);
      }
    },
    onColumnVisibilityChange: setColumnVisibility,
  });

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-6">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center gap-4 py-6 text-center">
          <AlertTriangle className="h-10 w-10 text-destructive" />
          <div className="space-y-1">
            <h3 className="font-semibold tracking-tight">
              {t("common.errors.loading")}
            </h3>
            <p className="text-sm text-muted-foreground">
              {t("common.errors.tryAgain")}
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() =>
              queryClient.invalidateQueries({ queryKey: ["user-audit-logs"] })
            }
          >
            {t("common.retry")}
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!logs?.content?.length) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center gap-4 py-6 text-center">
          <AlertCircle className="h-10 w-10 text-muted-foreground" />
          <div className="space-y-1">
            <h3 className="font-semibold tracking-tight">
              {t("common.noData.title")}
            </h3>
            <p className="text-sm text-muted-foreground">
              {t("common.noData.description")}
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>{t("settings.auditLogs.title")}</CardTitle>
            <CardDescription>
              {t("settings.auditLogs.description")}
            </CardDescription>
          </div>
          <DataTableViewOptions table={table} />
        </div>
      </CardHeader>
      <CardContent>
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <TableHead key={header.id}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                    className="group"
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    {t("common.noData.title")}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        <DataTablePagination
          table={table}
          totalElements={logs?.totalElements || 0}
          totalPages={logs?.totalPages || 0}
        />
      </CardContent>

      <AlertDialog
        open={deleteConfirm.isOpen}
        onOpenChange={(isOpen) =>
          setDeleteConfirm((prev) => ({ ...prev, isOpen }))
        }
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("settings.auditLogs.delete.title")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("settings.auditLogs.delete.description")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>
              {t("common.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t("common.deleting")}
                </>
              ) : (
                t("common.delete")
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!selectedLog} onOpenChange={() => setSelectedLog(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden z-50">
          <DialogHeader className="z-50">
            <DialogTitle>{t("settings.auditLogs.details.title")}</DialogTitle>
            <DialogDescription>
              {selectedLog &&
                format(new Date(selectedLog.createdAt), "d MMMM yyyy HH:mm", {
                  locale: enUS,
                })}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 relative z-50">
            {selectedLog && renderChanges(selectedLog.details)}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!selectedCode} onOpenChange={() => setSelectedCode(null)}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden z-50">
          <DialogHeader className="z-50">
            <DialogTitle>{selectedCode?.title}</DialogTitle>
          </DialogHeader>
          <div className="relative mt-4 z-50">
            {selectedCode && <CodeView code={selectedCode.code} />}
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
